import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { PunchupTile } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { POOL_OVERSUBSCRIBED_MESSAGE } from 'features/common/utils/poolCopy';
import { SendPool } from 'features/PunchupPages/SendTickets/poolBudget';

import styles from './SendTicketsPoolTiles.module.css';

export interface SendTicketsPoolTileProps {
	pool: SendPool;
	/** True when the pool's requested quantity exceeds its remaining (bulk mode) — drives the over-reach ring. */
	isExceeded: boolean;
	/** Shared by-name colour resolver from `useTicketTypeColors`, lifted to the parent so the hook runs once. */
	resolveByName: (name: string | null | undefined) => string;
}

/**
 * One pool's read-only availability tile. Split out of `SendTicketsPoolTiles` so the `tagSlot`, `topRight`, and
 * `helpButton` nodes are built in `useMemo`s with stable references per pool — `PunchupTile` is memoised, so a
 * fresh node object each parent render would defeat its memo (the `tagSlot` JSDoc mandates a useMemo callsite).
 * Each node only recomputes when the scalar it derives from changes.
 */
export const SendTicketsPoolTile: FC<SendTicketsPoolTileProps> = ({ pool, isExceeded, resolveByName }) => {
	const { t } = useTranslation();

	const topRight = useMemo(
		() =>
			pool.overCapacity ? (
				<AlertTriangle
					size={16}
					className={styles.warningIcon}
					aria-label={t('send-tickets-pool-over', 'Over capacity')}
				/>
			) : undefined,
		[pool.overCapacity, t]
	);

	const tagSlot = useMemo(
		() =>
			pool.memberNames.length > 0
				? pool.memberNames.map((member) => (
						<PunchupTicketTypeTag key={member} name={member} color={resolveByName(member)} />
				  ))
				: undefined,
		[pool.memberNames, resolveByName]
	);

	const helpButton = useMemo(
		() => (
			<PunchupHelpButton
				mainText={t(
					'send-tickets-pool-tip',
					'These tiers share one pool. Across them you can send up to the pool total — not each tier’s own count.'
				)}
				codeText={`${t('send-tickets-pool-available', 'available')} = ${pool.sellableRemaining}\n${t(
					'send-tickets-pool-issued',
					'already issued'
				)} = ${pool.issued} / ${pool.totalAmount}`}
				secondaryText={pool.overCapacity ? POOL_OVERSUBSCRIBED_MESSAGE : undefined}
			/>
		),
		[t, pool.sellableRemaining, pool.issued, pool.totalAmount, pool.overCapacity]
	);

	return (
		<div className={isExceeded ? styles.exceededWrapper : undefined} data-exceeded={isExceeded || undefined}>
			<PunchupTile
				title={pool.name}
				singleLineTitle
				compact
				value={String(pool.sellableRemaining)}
				topRight={topRight}
				tagSlot={tagSlot}
				helpButton={helpButton}
				dataCy={`send-pool-tile-${pool.poolId}`}
			/>
		</div>
	);
};
